import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm, useController } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import {
  WHY_GROOVE_OPTIONS,
  NUMBER_OF_EMPLOYEES_OPTIONS_V6,
} from 'subapps/onboarding/sharedForm/constants'
import { selectIsOauthAuthenticatable } from 'ducks/currentUser/selectors'
import { selectAccountIsOnboardingCompleted } from 'selectors/app'

import { styles } from './styles'
import { doSaveAndRedirect } from './actions'
import { styles as companyPageStyles } from '../companyPage/styles'

const NUMBER_OF_EMPLOYEES = 'numberOfEmployees'
const WHY_GROOVE = 'whyGroove'

// Validation schema
const FORM_SCHEMA = yup.object().shape({
  [NUMBER_OF_EMPLOYEES]: yup.string().required(),
  [WHY_GROOVE]: yup.array().of(yup.string()),
})

const defaultValues = {
  [NUMBER_OF_EMPLOYEES]: '',
  [WHY_GROOVE]: [],
}

const CompanyPage2 = () => {
  const dispatch = useDispatch()

  const {
    register,
    handleSubmit,
    control,
    formState: { isDirty, isValid, isSubmitting },
  } = useForm({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(FORM_SCHEMA),
  })
  const {
    field: { onChange: onWhyGrooveOptionsChange, value: whyGrooveOptions },
  } = useController({
    name: WHY_GROOVE,
    control,
  })
  const isOnboardingComplete = useSelector(selectAccountIsOnboardingCompleted)
  const submitDisabled =
    !isDirty || !isValid || isSubmitting || isOnboardingComplete
  const isLastStep = useSelector(selectIsOauthAuthenticatable)
  const buttonText = isLastStep ? 'Take me to my account' : 'Next'

  const onSubmit = useCallback(
    async data => {
      // await, so isSubmitting can be true during submitting
      await dispatch(doSaveAndRedirect(data))
    },
    [dispatch]
  )

  const handleWhyGrooveChange = useCallback(
    ({ target }) => {
      let value = [...whyGrooveOptions]
      if (target.checked && !whyGrooveOptions.includes(target.name)) {
        value.push(target.name)
      } else if (!target.checked) {
        value = value.filter(option => option !== target.name)
      }
      onWhyGrooveOptionsChange(value)
    },
    [onWhyGrooveOptionsChange, whyGrooveOptions]
  )

  return (
    <div css={styles.container}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <h4 className="grui" css={companyPageStyles.heading}>
          What do you want to use Groove for?{' '}
          <span>(select all that apply)</span>
        </h4>
        <div css={companyPageStyles.radios}>
          {WHY_GROOVE_OPTIONS.map(radio => (
            <div
              css={[companyPageStyles.radioBtn, styles.teammatesRaidoBtn]}
              key={radio.value}
            >
              <input
                id={radio.value}
                type="checkbox"
                name={radio.value}
                onChange={handleWhyGrooveChange}
              />
              <label htmlFor={radio.value}>{radio.text}</label>
            </div>
          ))}
        </div>

        <h4 className="grui mt-13" css={companyPageStyles.heading}>
          What is your team size?
        </h4>
        <div css={companyPageStyles.radios}>
          {NUMBER_OF_EMPLOYEES_OPTIONS_V6.map(radio => (
            <div
              css={[companyPageStyles.radioBtn, styles.teammatesRaidoBtn]}
              key={radio.key}
            >
              <input
                id={radio.key}
                type="radio"
                value={radio.value}
                {...register(NUMBER_OF_EMPLOYEES)}
              />
              <label htmlFor={radio.key}>{radio.text}</label>
            </div>
          ))}
        </div>

        <div className="grui mt-13">
          <Button
            htmlType="submit"
            type="info"
            size="xBig"
            css={companyPageStyles.submitButton}
            disabled={submitDisabled}
          >
            {buttonText}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default CompanyPage2
